// Single source of truth for shell route classification (PR2b.1).
//
// Route classification used to live in FIVE places that silently disagreed
// (v2-routes, workspace-sections, WorkspaceShell.shellTabForPath, workspace-tabs).
// Adding one route meant editing all five. This collapses them into ONE plain
// array of descriptors; the legacy helper functions now delegate here, keeping
// their names/signatures so call sites don't churn.
//
// IMPORTANT — the historical predicate DIVERGENCES are intentional and preserved
// per-entry (they are different concepts: viewport-clamp gate vs open-tab-vs-
// navigate vs deep-link-tab-registration). Each descriptor declares the exact
// per-classifier facts so the divergence is explicit in ONE table instead of
// drifting across five files. This is a behavior-preserving refactor; do not
// "fix" or unify the divergences here.

import {
  BRAND_ARTIFACT_META,
  buildBrandArtifactPath,
  parseBrandArtifactPath,
} from "./brand-navigation";
import type { WorkspaceSection } from "./workspace-sections";
import type { WorkTab } from "./workspace-tabs";
import { isWorkspaceReturnSource } from "./workspace-return";

/** Captured pieces of a matched path (brandId / artifactType / id / seg). */
export interface ShellRouteMatch {
  brandId?: string;
  artifactType?: string;
  /** Durable output family for `/outputs/:family/:key` work objects. */
  family?: string;
  id?: string;
  seg?: string;
}

export interface ShellRouteDescriptor {
  /** Human label for the row (debugging aid), e.g. "chat", "assets". */
  id: string;
  /** Returns the captured match, or null when the path doesn't match. */
  match(pathname: string): ShellRouteMatch | null;
  /** Drives sectionForPath. */
  section: WorkspaceSection;
  /** False when destination navigation owns context and a shell panel would duplicate it. */
  shellPanel?: boolean;
  /** Drives isV2WorkspaceRoute. */
  isV2Workspace: boolean;
  /** Drives isViewportClampedWorkspaceRoute. */
  viewportClamped?: boolean;
  /** Drives isInShellRoute (open-as-tab vs plain navigation). */
  isInShellRoute: boolean;
  /** Present => shellTabForPath returns this tab for the path. */
  tab?(m: ShellRouteMatch, route: string): WorkTab | null;
  /**
   * Returns the semantic query state that belongs in this work object's restore
   * route. Incidental query parameters (for example UTM/ref) must not make an
   * open tab retain tracking or one-off navigation context.
   */
  tabSearch?(search: string): string;
}

/** Keep only named, non-empty query parameters in a canonical order. */
function semanticSearch(search: string, keys: readonly string[]): string {
  const source = new URLSearchParams(search);
  const retained = new URLSearchParams();
  for (const key of keys) {
    const value = source.get(key);
    if (value) retained.set(key, value);
  }
  const encoded = retained.toString();
  return encoded ? `?${encoded}` : "";
}

/** Preserve a durable handoff source only when it is one of our known indexes. */
function semanticWorkspaceSearch(search: string): string {
  const source = new URLSearchParams(search);
  const retained = new URLSearchParams();
  const brandId = source.get("brandId");
  const returnTo = source.get("return_to");
  if (brandId) retained.set("brandId", brandId);
  if (isWorkspaceReturnSource(returnTo)) retained.set("return_to", returnTo);
  const encoded = retained.toString();
  return encoded ? `?${encoded}` : "";
}

const exact =
  (path: string) =>
  (pathname: string): ShellRouteMatch | null =>
    pathname === path ? {} : null;

export const SHELL_ROUTES: ShellRouteDescriptor[] = [
  {
    id: "publishing-studio",
    match: pathname => { const m = pathname.match(/^\/brand\/([^/]+)\/(publishing|youtube)(?:\/|$)/); return m ? { brandId: m[1] } : null; },
    // The studio owns its destination navigation; no second shell context panel.
    section: "workflows", shellPanel: false, isV2Workspace: true, isInShellRoute: false,
  },
  {
    id: "home",
    match: exact("/home"),
    section: "home",
    isV2Workspace: true,
    isInShellRoute: false,
  },
  {
    id: "tasks",
    match: exact("/tasks"),
    section: "tasks",
    isV2Workspace: true,
    isInShellRoute: false,
    tabSearch: (search) => semanticSearch(search, ["category"]),
    tab: (_m, pathname) => ({
      id: "tasks:catalog",
      kind: "tasks",
      label: "Actions",
      route: pathname,
    }),
  },
  {
    // Running an Action is a focused continuation of Action discovery, not a
    // second work object or a dialog. Keep the Tasks shell context while the
    // marketer completes the declared setup contract.
    id: "tasks-start-job",
    match: (pathname) => {
      const m = pathname.match(/^\/tasks\/start\/([^/]+)$/);
      return m ? { id: m[1] } : null;
    },
    section: "tasks",
    isV2Workspace: true,
    isInShellRoute: false,
    tabSearch: (search) => semanticSearch(search, ["brandId"]),
  },
  {
    id: "workflows",
    match: exact("/workflows"),
    section: "workflows",
    isV2Workspace: true,
    isInShellRoute: false,
    tab: (_m, pathname) => ({
      id: "workflows:queues",
      kind: "workflows",
      label: "Operations",
      route: pathname,
    }),
  },
  {
    id: "brand-review",
    match: (pathname) => {
      const m = pathname.match(/^\/brand\/([^/]+)\/review$/);
      return m ? { brandId: m[1] } : null;
    },
    // Review is workflow work: keep the compact workspace rail and highlight
    // Workflows instead of introducing a second top-level Review door.
    section: "workflows",
    isV2Workspace: true,
    isInShellRoute: false,
    tab: (m, pathname) => ({
      id: `brand-review:${m.brandId}`,
      kind: "review",
      label: "Review",
      route: pathname,
    }),
  },
  {
    // Data Views is the marketer-facing collection: one bounded question and
    // source scope at a time. Its saved-presentation review is a contextual
    // Analytics destination too, not a competing work-object tab. Existing
    // /analytics property URLs stay intact as provenance/detail destinations.
    id: "brand-data-views",
    match: (pathname) => {
      const m = pathname.match(/^\/brand\/([^/]+)\/data-views(?:\/variants\/[^/]+)?$/);
      return m ? { brandId: m[1] } : null;
    },
    section: "analytics",
    isV2Workspace: true,
    isInShellRoute: false,
    tabSearch: (search) => semanticSearch(search, ["view", "report", "surface", "source_assignment", "start_date", "end_date", "compare", "period", "detail", "page", "search", "sort", "direction", "attention", "ctr_limit", "impressions_min", "report_page", "change_status", "change_metric", "return_to"]),
  },
  {
    // The analytics INDEX is a property picker. Per §7.1.3 a list/index route stays a
    // standalone destination rather than becoming a work tab.
    id: "brand-analytics",
    match: (pathname) => {
      const m = pathname.match(/^\/brand\/([^/]+)\/analytics$/);
      return m ? { brandId: m[1] } : null;
    },
    // SEO & Analytics is its own section: search positions, impressions, CTR,
    // ranking keywords (GSC), and later onsite traffic/referrers (GA4, Parent 8).
    section: "analytics",
    isV2Workspace: true,
    isInShellRoute: false,
  },
  {
    // Assignment settings is contextual configuration, not a property work tab.
    id: "brand-analytics-settings",
    match: (pathname) => {
      const m = pathname.match(/^\/brand\/([^/]+)\/analytics\/settings\/([^/]+)$/);
      return m ? { brandId: m[1], id: m[2] } : null;
    },
    section: "analytics",
    isV2Workspace: true,
    isInShellRoute: false,
  },
  {
    // Everything BELOW the property is one work object: a property investigation.
    //
    // Previously only the exact `/analytics` path was registered, so the four deeper
    // routes resolved to nothing (PRD-0096 §A.4 E1). The consequences were all silent:
    // routeSection() returned null so the sub-toolbar read "Workspace" (E2); the route
    // was not a V2 workspace route so the full-bleed shell rendered inside page padding
    // (E3); and no tab descriptor meant the work never appeared in the strip (E4).
    //
    // The tab id is keyed at the PROPERTY (§A.16): drilling property -> source -> keyword
    // -> referrer is navigation within one investigation, not four work objects. The
    // route moves with the operator, the id does not, so the strip keeps one entry and
    // re-activating it restores the deepest position reached.
    id: "brand-analytics-property",
    tabSearch: (search) => semanticSearch(search, ["view", "report", "surface", "source_assignment", "start_date", "end_date", "compare", "period", "detail", "page", "search", "sort", "direction", "attention", "ctr_limit", "impressions_min", "report_page", "change_status", "change_metric", "return_to"]),
    match: (pathname) => {
      const m = pathname.match(/^\/brand\/([^/]+)\/analytics\/property\/([^/]+)(?:\/|$)/);
      return m ? { brandId: m[1], id: m[2] } : null;
    },
    section: "analytics",
    isV2Workspace: true,
    isInShellRoute: true,
    tab: (m, pathname) => ({
      id: `brand-analytics:${m.brandId}:${m.id}`,
      kind: "analytics",
      label: analyticsTabLabel(pathname),
      route: pathname,
    }),
  },
  {
    id: "chat",
    match: (pathname) => {
      const m = pathname.match(/^\/agent\/([^/]+)$/);
      return m ? { brandId: m[1] } : null;
    },
    section: "chat",
    isV2Workspace: true,
    viewportClamped: true,
    isInShellRoute: true,
    tab: (m, pathname) => ({
      id: `chat:${m.brandId}`,
      kind: "chat",
      label: "Chat",
      route: pathname,
    }),
  },
  {
    id: "docs",
    match: (pathname) => {
      const m = pathname.match(/^\/brand\/([^/]+)\/docs$/);
      return m ? { brandId: m[1] } : null;
    },
    section: "docs",
    isV2Workspace: true,
    isInShellRoute: true,
    tab: (m, pathname) => ({
      id: `docs:${m.brandId}`,
      kind: "docs",
      label: "Brand Docs",
      route: pathname,
    }),
  },
  {
    id: "assets",
    match: (pathname) => {
      const m = pathname.match(/^\/brand\/([^/]+)\/assets$/);
      return m ? { brandId: m[1] } : null;
    },
    section: "assets",
    isV2Workspace: true,
    isInShellRoute: true,
    tab: (m, pathname) => ({
      id: `assets:${m.brandId}`,
      kind: "assets",
      label: "Assets",
      route: pathname,
    }),
    // Collection selection is local Assets state. Do not retain filter/sort,
    // tracking, or referral parameters when restoring this tab.
    tabSearch: (search) => semanticSearch(search, ["collection_id"]),
  },
  {
    // Shopify imports list/detail are SECTIONS of Assets that live UNDER the shell
    // (PR2b.1 regression fix): the shell chrome applies (isV2Workspace) and stays
    // mounted, the rail keeps Assets highlighted (section: "assets"), but these are
    // NOT open-as-tab work objects (isInShellRoute false) and register NO tab (no
    // `tab`). So navigating Assets → imports keeps the shell mounted and leaves the
    // operator's open work tabs untouched instead of unmounting the shell.
    id: "assets-imports",
    match: (pathname) => {
      const m = pathname.match(/^\/brand\/([^/]+)\/assets\/imports$/);
      return m ? { brandId: m[1] } : null;
    },
    section: "assets",
    isV2Workspace: true,
    isInShellRoute: false,
  },
  {
    id: "assets-imports-detail",
    match: (pathname) => {
      const m = pathname.match(/^\/brand\/([^/]+)\/assets\/imports\/([^/]+)$/);
      return m ? { brandId: m[1], id: m[2] } : null;
    },
    section: "assets",
    isV2Workspace: true,
    isInShellRoute: false,
  },
  {
    // The outputs collection is a library/index, which remains a standalone
    // destination under the V2 IA rule (§7.1.3). Individual output details are
    // durable work objects and therefore have their own descriptor below.
    id: "output-detail",
    match: (pathname) => {
      const m = pathname.match(/^\/outputs\/([^/]+)\/([^/]+)$/);
      return m ? { family: m[1], id: m[2] } : null;
    },
    section: "workflows",
    isV2Workspace: true,
    isInShellRoute: true,
    tab: (m, pathname) => ({
      // family + key are the output's durable identity. Keep the encoded key:
      // it is the canonical URL-safe storage key and preserves slash-bearing
      // agent artifact keys as one object rather than a route hierarchy.
      id: `output:${m.family}:${m.id}`,
      kind: "output",
      label: "Output",
      route: pathname,
    }),
    // Output details are global paths. Preserve their explicit active-brand
    // scope for close/reopen restoration, while dropping tracking/ref state.
    tabSearch: semanticWorkspaceSearch,
  },
  {
    id: "operator-work",
    match: (pathname) => {
      const m = pathname.match(/^\/review\/([^/]+)/);
      return m ? { seg: "review", id: m[1] } : null;
    },
    section: "workflows",
    isV2Workspace: true,
    isInShellRoute: true,
    tab: (m, pathname) => ({
      id: `review-run:${m.id}`,
      kind: "review",
      label: "Review",
      route: pathname,
    }),
    // Global work routes have no brand path segment; brandId restores their
    // active-brand scope without allowing incidental query state to persist.
    tabSearch: (search) => semanticSearch(search, ["brandId"]),
  },
  {
    id: "run-work",
    match: (pathname) => {
      const m = pathname.match(/^\/runs\/([^/]+)(?:\/trace)?$/);
      return m ? { seg: "runs", id: m[1] } : null;
    },
    // A Result is a durable work object owned by Runs. Operations is a
    // deliberate secondary destination, never the shell context of a result.
    section: "activity",
    isV2Workspace: true,
    isInShellRoute: true,
    tab: (m, pathname) => ({
      id: `runs:${m.id}`,
      kind: "output",
      label: pathname.endsWith("/trace") ? "Traces" : "Result",
      route: pathname,
    }),
    tabSearch: semanticWorkspaceSearch,
  },
  {
    id: "gate-work",
    match: (pathname) => {
      const m = pathname.match(/^\/gates\/([^/]+)/);
      return m ? { seg: "gates", id: m[1] } : null;
    },
    section: "workflows",
    isV2Workspace: false,
    isInShellRoute: true,
    tab: (m, pathname) => ({ id: `gates:${m.id}`, kind: "review", label: "Gate review", route: pathname }),
    tabSearch: (search) => semanticSearch(search, ["brandId"]),
  },
  {
    id: "artifact",
    match: (pathname) => {
      const parsed = parseBrandArtifactPath(pathname);
      return parsed ? { brandId: parsed.brandId, artifactType: parsed.artifactType } : null;
    },
    // Brand Docs section also covers the artifact routes (strategy/voice).
    section: "docs",
    isV2Workspace: true,
    isInShellRoute: false,
    tab: (m) => {
      const artifactType = m.artifactType as keyof typeof BRAND_ARTIFACT_META;
      const brandId = m.brandId!;
      return {
        id: `artifact:${artifactType}:${brandId}`,
        kind: "artifact",
        label: BRAND_ARTIFACT_META[artifactType].tabLabel,
        route: buildBrandArtifactPath(brandId, artifactType),
      };
    },
  },
];

/** First-matching descriptor for a path, plus its captured match. */
function findRoute(
  pathname: string,
): { descriptor: ShellRouteDescriptor; match: ShellRouteMatch } | null {
  for (const descriptor of SHELL_ROUTES) {
    const match = descriptor.match(pathname);
    if (match) return { descriptor, match };
  }
  return null;
}

export function matchShellRoute(pathname: string): ShellRouteDescriptor | null {
  return findRoute(pathname)?.descriptor ?? null;
}

export function routeIsV2Workspace(pathname: string): boolean {
  return findRoute(pathname)?.descriptor.isV2Workspace ?? false;
}

export function routeViewportClamped(pathname: string): boolean {
  return findRoute(pathname)?.descriptor.viewportClamped ?? false;
}

export function routeIsInShell(pathname: string): boolean {
  return findRoute(pathname)?.descriptor.isInShellRoute ?? false;
}

/**
 * Label for an analytics property tab. Reflects the operator's current depth so the strip
 * shows where they are, while the tab's identity stays keyed at the property (§A.16).
 */
function analyticsTabLabel(pathname: string): string {
  const keyword = pathname.match(/\/keyword\/([^/]+)/);
  if (keyword) return `Analytics · ${decodeURIComponent(keyword[1]!)}`;
  const referrer = pathname.match(/\/source\/referrer\/[^/]+\/([^/]+)/);
  if (referrer) return `Analytics · ${decodeURIComponent(referrer[1]!)}`;
  if (/\/source\/native\//.test(pathname)) return "Analytics · source";
  return "Analytics";
}

export function routeSection(pathname: string): WorkspaceSection {
  return findRoute(pathname)?.descriptor.section ?? null;
}

export function routeTab(pathname: string): WorkTab | null {
  return routeTabForUrl(pathname);
}

/**
 * Resolve a work tab from the complete browser route. A descriptor can retain
 * only its explicitly declared semantic query state in the restore point. Most
 * descriptors retain none: identity, section, and restore route then come from
 * the pathname alone. This keeps tabs stable while preventing UTM/ref and
 * other incidental navigation parameters from becoming durable tab state.
 */
export function routeTabForUrl(pathname: string, search = ""): WorkTab | null {
  const found = findRoute(pathname);
  if (!found?.descriptor.tab) return null;
  return found.descriptor.tab(found.match, `${pathname}${found.descriptor.tabSearch?.(search) ?? ""}`);
}

/** Canonical Assets work-object descriptor for panel and shell callers. */
export function buildAssetsWorkTab(brandId: string, collectionId?: string): WorkTab {
  const pathname = `/brand/${encodeURIComponent(brandId)}/assets`;
  const search = collectionId ? `?collection_id=${encodeURIComponent(collectionId)}` : "";
  const tab = routeTabForUrl(pathname, search);
  if (!tab) throw new Error("Assets route is missing a shell tab descriptor");
  return tab;
}

/** Canonical Analytics investigation descriptor for panel and shell callers. */
export function buildAnalyticsPropertyWorkTab(
  brandId: string,
  surfaceId: string,
  label?: string,
): WorkTab {
  const pathname = `/brand/${encodeURIComponent(brandId)}/analytics/property/${encodeURIComponent(surfaceId)}`;
  const tab = routeTabForUrl(pathname);
  if (!tab) throw new Error("Analytics property route is missing a shell tab descriptor");
  return label ? { ...tab, label } : tab;
}

/** Registry-owned context-panel opt-out; all existing routes retain their panel behavior. */
export function routeShellPanelEnabled(pathname: string): boolean {
  return findRoute(pathname)?.descriptor.shellPanel !== false;
}
